import React from "react";
import { useTranslation } from "react-i18next";
import { askSourcePage, useAskSidebar } from "../../chat/AskContext";
import AskConversation from "./AskConversation";
import { ResizeDivider, ResizeHairline, useRightPanelWidth } from "../ResizeDivider";
import { AskConversationActions, AskPanelActions } from "./AskActions";
import RecentQuestions from "../watchlist/RecentQuestions";

const MIN_WIDTH = 320;
const MAX_WIDTH = 720;

export function AskSidebar() {
  const { t } = useTranslation();
  const {
    open,
    initialized,
    expanded,
    docked,
    conversationId,
    openQuestion,
    history,
    setHistory,
    target,
    page,
    watchRequest,
  } = useAskSidebar();
  const source = askSourcePage(target, page);
  const { width, resizing, startResize, onResizeKeyDown, reset } = useRightPanelWidth({
    initial: 440,
    min: MIN_WIDTH,
    max: MAX_WIDTH,
  });
  if (!initialized) return null;
  return (
    <div
      className={
        open
          ? `flex min-h-0 min-w-0 ${expanded ? "flex-1" : docked ? "absolute inset-y-0 right-0 z-30 w-full shadow-xl lg:static lg:w-auto lg:shrink-0 lg:shadow-none" : "absolute inset-y-0 right-0 z-30 w-full shadow-xl lg:w-auto"}`
          : "hidden"
      }
      style={{ "--ask-width": `${width}px` } as React.CSSProperties}
    >
      {!expanded && (
        <ResizeHairline>
          <ResizeDivider
            label={t("resize_sidebar")}
            title={t("resize_sidebar")}
            value={width}
            min={MIN_WIDTH}
            max={MAX_WIDTH}
            resizing={resizing}
            onPointerDown={startResize}
            onKeyDown={onResizeKeyDown}
            onDoubleClick={reset}
            className="hidden lg:flex"
            overlay
          />
        </ResizeHairline>
      )}
      <aside
        id="ask-sidebar"
        aria-label={t("dashboard.ask")}
        className={`flex min-h-0 min-w-0 w-full flex-col border-l border-outline bg-surface ${expanded ? "" : "lg:w-[var(--ask-width)] lg:max-w-[55vw]"}`}
      >
        {/* Keep the chat toolbar below the app bar in both panel and full-screen views. */}
        <header className="flex min-h-12 shrink-0 items-center gap-1 border-b border-outline bg-surface px-4">
          <AskConversationActions />
          <span className="mr-auto min-w-0 truncate px-1 text-xs text-text-dim">
            {(!watchRequest || history) &&
              source &&
              t("askPanel.context_from_page", { page: t(`askPanel.page_${source.kind}`) })}
          </span>
          <AskPanelActions />
        </header>
        {history && (
          <section
            aria-labelledby="recent-questions-heading"
            className="min-h-0 flex-1 overflow-y-auto p-4"
          >
            <h2 id="recent-questions-heading" className="mb-2 text-xs font-medium text-text-dim">
              {t("watchlist.recent_questions")}
            </h2>
            <RecentQuestions
              onOpen={question => {
                openQuestion({ ...question, questionId: question.id });
                setHistory(false);
              }}
            />
          </section>
        )}
        <div
          hidden={history}
          className={`${history ? "hidden" : "min-h-0 min-w-0 flex-1"} ${expanded ? "mx-auto w-full max-w-4xl" : ""}`}
        >
          <AskConversation key={conversationId} active={open && !history} />
        </div>
      </aside>
    </div>
  );
}
